import React, { useEffect, useRef, useState } from 'react';

import { dispatchToast } from '../../modal_host/events';
import SearchableCombobox from '../../../common/components/SearchableCombobox';
import { formatDayShiftLabel } from '../../../common/workflowTiming';
import TimelineWorkflowBrowserModal from './TimelineWorkflowBrowserModal';

function dispatchEntityAction(detail) {
  document.dispatchEvent(new CustomEvent('ui:entity-action', {
    detail,
  }));
}

function renderWorkflowRows(workflowTasks, workflowDetailLoading) {
  if (workflowDetailLoading) {
    return (
      <tr>
        <td colSpan="3" className="text-secondary text-center py-3">
          Lade Workflow-Tasks...
        </td>
      </tr>
    );
  }

  if (!workflowTasks || workflowTasks.length < 1) {
    return (
      <tr>
        <td colSpan="3" className="text-secondary text-center py-3">
          Kein Workflow ausgewählt
        </td>
      </tr>
    );
  }

  return workflowTasks.map((task) => (
    <tr key={String(task.id ?? `${task.task_id}:${task.position}`)}>
      <td>{task?.task?.name ?? '-'}</td>
      <td>{task?.duration_label ?? '-'}</td>
      <td>{task?.rel_offset_label ?? '-'}</td>
    </tr>
  ));
}

export default function TimelineSidebar({
  collapsed,
  onToggleCollapsed,
  workflowOptions,
  workflowsLoading,
  selectedWorkflowId,
  onSelectedWorkflowIdChange,
  workflowTasks,
  workflowDetailLoading,
  startDate,
  onStartDateChange,
  timeMode,
  onTimeModeChange,
  timeValue,
  onTimeValueChange,
  dayWorkflowPlanning,
  onApply,
  applying,
}) {
  const closeTimeoutRef = useRef(null);
  const infoTooltipRef = useRef(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [browserClosing, setBrowserClosing] = useState(false);
  const [browserInitialQuery, setBrowserInitialQuery] = useState('');

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const element = infoTooltipRef.current;
    const TooltipCtor = window.bootstrap?.Tooltip;
    if (!element || !TooltipCtor) {
      return undefined;
    }

    const tooltip = typeof TooltipCtor.getOrCreateInstance === 'function'
      ? TooltipCtor.getOrCreateInstance(element, {
        container: document.body,
        trigger: 'hover focus',
      })
      : new TooltipCtor(element, {
        container: document.body,
        trigger: 'hover focus',
      });

    return () => tooltip.dispose?.();
  }, []);

  useEffect(() => {
    function handleCrudCreated(event) {
      const entity = String(event?.detail?.entity ?? '');
      const source = String(event?.detail?.source ?? '');
      const createdWorkflowId = event?.detail?.record?.id;

      if (entity !== 'workflows' || source !== 'timeline-sidebar' || !createdWorkflowId) {
        return;
      }

      onSelectedWorkflowIdChange(String(createdWorkflowId));
    }

    document.addEventListener('crud:created', handleCrudCreated);
    return () => document.removeEventListener('crud:created', handleCrudCreated);
  }, [onSelectedWorkflowIdChange]);

  function openWorkflowBrowser(initialQuery = '') {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setBrowserInitialQuery(String(initialQuery ?? '').trim());
    setBrowserClosing(false);
    setBrowserOpen(true);
  }

  function requestCloseWorkflowBrowser() {
    if (!browserOpen) {
      return;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    setBrowserClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      setBrowserClosing(false);
      setBrowserOpen(false);
      closeTimeoutRef.current = null;
    }, 180);
  }

  function handleWorkflowBrowserSubmit(workflow) {
    if (!workflow?.id) {
      return;
    }

    onSelectedWorkflowIdChange(String(workflow.id));
    requestCloseWorkflowBrowser();
  }

  const startTimeValue = timeMode === 'start'
    ? String(timeValue ?? '')
    : String(dayWorkflowPlanning?.startTime ?? '');
  const endTimeValue = timeMode === 'end'
    ? String(timeValue ?? '')
    : String(dayWorkflowPlanning?.endTime ?? '');
  const startShiftLabel = formatDayShiftLabel(dayWorkflowPlanning?.startDayShift ?? 0);
  const endShiftLabel = formatDayShiftLabel(dayWorkflowPlanning?.endDayShift ?? 0);

  function openWorkflowCreate() {
    dispatchEntityAction({
      source: 'timeline-sidebar',
      entity: 'workflows',
      action: 'create',
    });
  }

  function openRegularWorkflowCreate() {
    if (!selectedWorkflowId) {
      dispatchToast({
        variant: 'warning',
        title: 'Timeline',
        message: 'Bitte zuerst einen Workflow auswählen.',
      });
      return;
    }

    dispatchEntityAction({
      source: 'timeline-sidebar',
      entity: 'regular-workflows',
      action: 'create',
      prefill: {
        workflow_id: selectedWorkflowId ? Number(selectedWorkflowId) : '',
        time_mode: timeMode,
        time_value: String(timeValue ?? ''),
        start_date: String(startDate ?? ''),
      },
    });
  }

  return (
    <>
      <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
        <button
          className="sidebar-toggle btn-outline-light"
          type="button"
          data-testid="timeline-sidebar-toggle"
          onClick={onToggleCollapsed}
          title={collapsed ? 'Sidebar einblenden' : 'Sidebar ausblenden'}
          aria-label={collapsed ? 'Sidebar einblenden' : 'Sidebar ausblenden'}
          aria-expanded={collapsed ? 'false' : 'true'}
        >
          <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
        </button>

        {collapsed ? (
          <button
            id="timelineMobileRunningTaskStatus"
            type="button"
            className="timeline-mobile-running-task-status timeline-mobile-running-task-status-inline d-none"
            title="Zur aktuellen Uhrzeit springen"
          >
            <span className="timeline-mobile-running-task-main">
              <span id="timelineMobileRunningTaskTitle" className="timeline-mobile-running-task-title">-</span>
              <span id="timelineMobileRunningTaskMeta" className="timeline-mobile-running-task-meta">-</span>
            </span>
            <span id="timelineMobileRunningTaskCountdown" className="timeline-mobile-running-task-countdown">-</span>
          </button>
        ) : null}

        <div className="sidebar-content">
          <div className="sidebar-section mb-4">
            <div className="sidebar-header">
              <div className="sidebar-section-heading">
                <div className="sidebar-section-heading-main">
                  <h5 className="mb-0">Workflow einplanen</h5>
                  <button
                    ref={infoTooltipRef}
                    type="button"
                    className="context-info-button sidebar-info-button"
                    aria-label="Informationen zum Einplanen von Tages-Workflows"
                    data-bs-toggle="tooltip"
                    data-bs-placement="right"
                    data-bs-title="Wähle einen Workflow, plane ihn für den ausgewählten Tag ein oder lege daraus eine Routine an."
                  >
                    <i className="bi bi-info-circle" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <SearchableCombobox
              options={workflowOptions}
              value={selectedWorkflowId}
              onChange={onSelectedWorkflowIdChange}
              loading={workflowsLoading}
              disabled={workflowsLoading}
              placeholder="Workflow auswählen..."
              ariaLabel="Workflow auswählen"
              emptyText="Kein passender Workflow gefunden"
              actionButtonTitle="Workflows durchsuchen und auswählen"
              actionButtonIconClassName="bi bi-table"
              actionButtonTestId="timeline-sidebar-workflow-browser"
              onActionButtonClick={openWorkflowBrowser}
            />

            <div className="sidebar-utility-actions mt-2">
              <button
                type="button"
                className="btn btn-link sidebar-utility-link"
                onClick={openWorkflowCreate}
                data-testid="timeline-sidebar-create-workflow"
                title="Workflow anlegen"
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                <span>Workflow anlegen</span>
              </button>
            </div>

            <div className="row g-2 mt-3">
              <div className="col-6 col-md-7">
                <label htmlFor="timelineSidebarDate" className="form-label">Datum</label>
                <input
                  id="timelineSidebarDate"
                  data-testid="timeline-sidebar-date"
                  type="date"
                  className="form-control"
                  value={startDate ?? ''}
                  onChange={(event) => onStartDateChange(event.target.value)}
                />
              </div>
              <div className="col-6 col-md-5">
                <label className="form-label d-block">Planung</label>
                <div
                  className="btn-group btn-group-sm w-100 workflow-time-mode-group"
                  role="group"
                  aria-label="Timeline-Day-Workflow-Zeitmodus"
                  data-time-mode={timeMode}
                >
                  <input
                    className="btn-check"
                    type="radio"
                    name="timelineDayWorkflowTimeMode"
                    id="timelineDayWorkflowTimeModeStart"
                    checked={timeMode === 'start'}
                    onChange={() => onTimeModeChange('start')}
                  />
                  <label className="btn btn-outline-light" htmlFor="timelineDayWorkflowTimeModeStart">Start</label>

                  <input
                    className="btn-check"
                    type="radio"
                    name="timelineDayWorkflowTimeMode"
                    id="timelineDayWorkflowTimeModeEnd"
                    checked={timeMode === 'end'}
                    onChange={() => onTimeModeChange('end')}
                  />
                  <label className="btn btn-outline-light" htmlFor="timelineDayWorkflowTimeModeEnd">Ende</label>
                </div>
              </div>
            </div>

            <div className="row g-2 mt-3">
              <div className={`col-6 workflow-time-field${timeMode !== 'start' ? ' workflow-time-field-derived' : ''}`}>
                <label htmlFor="timelineSidebarStartTime" className="form-label">Startzeit{startShiftLabel ? ` ${startShiftLabel}` : ''}</label>
                <input
                  id="timelineSidebarStartTime"
                  data-testid="timeline-sidebar-start-time"
                  type="time"
                  className={`form-control${timeMode !== 'start' ? ' workflow-time-input-derived' : ''}`}
                  value={startTimeValue}
                  onChange={(event) => onTimeValueChange(event.target.value)}
                  disabled={timeMode !== 'start'}
                />
              </div>
              <div className={`col-6 workflow-time-field${timeMode !== 'end' ? ' workflow-time-field-derived' : ''}`}>
                <label htmlFor="timelineSidebarEndTime" className="form-label">Endzeit{endShiftLabel ? ` ${endShiftLabel}` : ''}</label>
                <input
                  id="timelineSidebarEndTime"
                  data-testid="timeline-sidebar-end-time"
                  type="time"
                  className={`form-control${timeMode !== 'end' ? ' workflow-time-input-derived' : ''}`}
                  value={endTimeValue}
                  onChange={(event) => onTimeValueChange(event.target.value)}
                  disabled={timeMode !== 'end'}
                />
              </div>
            </div>

          </div>

          <div className="sidebar-section timeline-sidebar-tasks-section">
            <h6>Workflow-Tasks</h6>
            <div className="timeline-sidebar-task-table-wrap">
              <table className="table table-dark table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Dauer</th>
                    <th>Versatz</th>
                  </tr>
                </thead>
                <tbody>
                  {renderWorkflowRows(workflowTasks, workflowDetailLoading)}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="btn btn-outline-light w-100 mt-3"
              onClick={onApply}
              data-testid="timeline-sidebar-plan-day-workflow"
              disabled={applying}
              title="Ausgewählten Workflow für diesen Tag einplanen"
            >
              {applying ? 'Plane ein...' : 'Für diesen Tag einplanen'}
            </button>

            <button
              type="button"
              className="btn w-100 mt-2 sidebar-secondary-action"
              onClick={openRegularWorkflowCreate}
              data-testid="timeline-sidebar-create-regular-workflow"
              disabled={workflowsLoading}
              title={selectedWorkflowId ? 'Aus der aktuellen Planung eine Routine speichern' : 'Bitte zuerst einen Workflow auswählen'}
            >
              Als Routine speichern
            </button>
          </div>
        </div>
      </aside>

      {browserOpen ? (
        <TimelineWorkflowBrowserModal
          workflows={workflowOptions}
          selectedWorkflowId={selectedWorkflowId}
          initialQuery={browserInitialQuery}
          closing={browserClosing}
          onClose={requestCloseWorkflowBrowser}
          onSubmit={handleWorkflowBrowserSubmit}
          contextMenuSource="timeline-sidebar"
          overlayClassName="timeline-sidebar-browser-overlay"
        />
      ) : null}
    </>
  );
}
